import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, User, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OnboardingSession {
  id: string;
  status: string;
  created_at: string;
}

interface OnboardingItem {
  id: string;
  status: string;
  catalog_id: string | null;
  deal_id: string | null;
  eligibility: any;
  catalog?: {
    name: string;
    description: string | null;
  } | null;
  deal?: {
    id: string;
    deal_name: string | null;
    contacts?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
    } | null;
    owner?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      display_name: string | null;
    } | null;
  } | null;
  onboarding_sessions?: OnboardingSession[];
}

interface OnboardingCardProps {
  item: OnboardingItem;
  onView: () => void;
  compact?: boolean;
}

const getLatestSession = (sessions?: OnboardingSession[]) => {
  if (!sessions || sessions.length === 0) return null;
  return sessions.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];
};

const getContactName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.contacts) return 'Cliente não informado';
  const { first_name, last_name } = deal.contacts;
  return [first_name, last_name].filter(Boolean).join(' ') || 'Cliente não informado';
};

const getOwnerName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.owner) return 'Não atribuído';
  const { display_name, first_name, last_name } = deal.owner;
  return display_name || [first_name, last_name].filter(Boolean).join(' ') || 'Não atribuído';
};

const getStatusBadge = (item: OnboardingItem) => {
  const latestSession = getLatestSession(item.onboarding_sessions);
  
  if (item.status === "pending_onboarding") {
    if (!latestSession || ['cancelled', 'completed'].includes(latestSession.status)) {
      return <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">Aguardando</Badge>;
    }
    if (latestSession.status === 'scheduled') {
      return <Badge className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">Agendado</Badge>;
    }
    if (latestSession.status === 'in_progress') {
      return <Badge className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">Em Andamento</Badge>;
    }
  }
  
  if (item.status === "onboarded") {
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">Onboarded</Badge>;
  }
  
  if (item.status === "active") {
    return <Badge className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">Ativo</Badge>;
  }
  
  return <Badge variant="outline">{item.status}</Badge>;
};

const getButtonText = (item: OnboardingItem) => {
  const latestSession = getLatestSession(item.onboarding_sessions);
  
  if (!latestSession || ['cancelled', 'completed'].includes(latestSession.status)) {
    return "Agendar";
  }
  if (latestSession.status === 'scheduled') {
    return "Iniciar";
  }
  if (latestSession.status === 'in_progress') {
    return "Continuar";
  }
  return "Ver";
};

export const OnboardingCard: React.FC<OnboardingCardProps> = ({ item, onView, compact = false }) => {
  if (compact) {
    return (
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow bg-card"
        onClick={onView}
      >
        <CardContent className="p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium text-sm truncate">
                {item.catalog?.name || 'Produto não informado'}
              </span>
            </div>
            {getStatusBadge(item)}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {getContactName(item.deal)}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow bg-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-medium truncate">
                {item.catalog?.name || 'Produto não informado'}
              </h3>
              <p className="text-sm text-muted-foreground truncate">
                {item.deal?.deal_name || 'Deal não informado'}
              </p>
            </div>
          </div>
          {getStatusBadge(item)}
        </div>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Cliente:</span>
            <span className="truncate">{getContactName(item.deal)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Vendedor:</span>
            <span className="truncate">{getOwnerName(item.deal)}</span>
          </div>
        </div>
        
        <Button 
          onClick={onView}
          className="w-full"
          variant="outline"
        >
          {getButtonText(item)}
        </Button>
      </CardContent>
    </Card>
  );
};
